import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/providers/context/AuthProvider';
import { useGenerateSSOToken } from '@/apis/auth/hook';
import { GenerateSSOTokenResponse } from '@/apis/auth/types';

const loadFeatureBaseSDK = (): Promise<void> => {
  return new Promise<void>((resolve) => {
    const win = window as any;

    if (win.Featurebase) {
      resolve();
      return;
    }

    let script = document.getElementById('featurebase-sdk') as HTMLScriptElement;

    if (!script) {
      script = document.createElement('script');
      script.id = 'featurebase-sdk';
      script.src = 'https://do.featurebase.app/js/sdk.js';
      script.async = true;
      document.head.appendChild(script);
    }

    const checkInterval = setInterval(() => {
      if (win.Featurebase) {
        clearInterval(checkInterval);
        resolve();
      }
    }, 100);

    setTimeout(() => {
      clearInterval(checkInterval);
      resolve();
    }, 10000);
  });
};

const initializeFeatureBaseWidgets = (ssoSupportToken: string, identity?: GenerateSSOTokenResponse['identity']) => {
  const win = window as any;

  if (!win.Featurebase) {
    console.warn('FeatureBase SDK failed to load');
    return;
  }

  try {
    win.Featurebase('boot', {
      appId: '68430292a62bfc928f502d6c',
      theme: 'light',
      language: 'en',
      featurebaseJwt: ssoSupportToken
    });

    win.Featurebase('identify', {
      organization: 'supportkickcall',
      userId: identity?.user_id,
      email: identity?.email,
      name: identity?.name,
      featurebaseJwt: ssoSupportToken,
      company: {
        id: identity?.company_id,
        name: identity?.company_name
      }
    });

    win.Featurebase(
      'initialize_survey_widget',
      {
        organization: 'supportkickcall',
        placement: 'bottom-right',
        theme: 'light',
        locale: 'en',
        featurebaseJwt: ssoSupportToken
      },
      (err: any) => {
        if (err) {
          console.error('Featurebase survey widget error:', err);
        }
      }
    );
  } catch (error) {
    console.error('Error initializing Featurebase widgets:', error);
  }
};

const clearFeaturebaseWidgets = () => {
  const win = window as any;

  if (win.Featurebase) {
    try {
      win.Featurebase('destroy');
    } catch (error) {
      console.warn('Error destroying Featurebase widgets:', error);
    }
  }

  const featurebaseElements = document.querySelectorAll(
    '[data-featurebase], [id*="featurebase"], [class*="featurebase"]'
  );
  featurebaseElements.forEach((element) => element.remove());

  const sdkScript = document.getElementById('featurebase-sdk');
  if (sdkScript) {
    sdkScript.remove();
  }

  const featurebaseIframes = document.querySelectorAll('iframe[src*="featurebase"]');
  featurebaseIframes.forEach((iframe) => iframe.remove());
};

const IDENTITY_STORAGE_KEY = 'featurebase_identity';

const FeaturebaseWidget = () => {
  const { isAuthenticated, ssoSupportToken } = useAuth();
  const hasFetched = useRef(false);
  const [identity, setIdentity] = useState<GenerateSSOTokenResponse['identity'] | undefined>(() => {
    // Load identity from localStorage on mount if it exists
    try {
      const stored = localStorage.getItem(IDENTITY_STORAGE_KEY);
      return stored ? JSON.parse(stored) : undefined;
    } catch {
      return undefined;
    }
  });

  const { mutateAsync: generateSSOToken, isPending } = useGenerateSSOToken();

  // Load identity from localStorage when ssoSupportToken is available but identity state is not
  useEffect(() => {
    if (ssoSupportToken && !identity) {
      try {
        const stored = localStorage.getItem(IDENTITY_STORAGE_KEY);
        if (stored) {
          setIdentity(JSON.parse(stored));
        }
      } catch {
        console.log('identity parser issue');
      }
    }
  }, [ssoSupportToken, identity]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearFeaturebaseWidgets();
      // Clear identity from localStorage on logout
      localStorage.removeItem(IDENTITY_STORAGE_KEY);
      setIdentity(undefined);
      return;
    }
    if (!ssoSupportToken && !isPending && !hasFetched.current) {
      hasFetched.current = true;
      generateSSOToken().then((response) => {
        if (response?.identity) {
          setIdentity(response.identity);
          // Store identity in localStorage for persistence
          localStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(response.identity));
        }
      });
      return;
    }

    if (!ssoSupportToken || isPending) {
      return;
    }

    const initializeWidgets = async () => {
      await loadFeatureBaseSDK();
      initializeFeatureBaseWidgets(ssoSupportToken!, identity);
    };

    initializeWidgets();

    return () => {
      clearFeaturebaseWidgets();
    };
  }, [isAuthenticated, ssoSupportToken, isPending, generateSSOToken, identity]);

  return <div data-featurebase-embed />;
};

export default FeaturebaseWidget;
